import { utcToZonedTime, format } from 'date-fns-tz';

export const stateToTzMap: Record<string, string> = {
  AL: 'America/Chicago',
  AK: 'America/Anchorage', // Aleutians are America/Adak
  AS: 'Pacific/Pago_Pago',
  AZ: 'America/Phoenix', // no DST
  AR: 'America/Chicago',
  CA: 'America/Los_Angeles',
  CO: 'America/Denver',
  CT: 'America/New_York',
  DE: 'America/New_York',
  DC: 'America/New_York',
  FM: 'Pacific/Chuuk', // FSM spans Chuuk/Pohnpei/Kosrae; see notes
  FL: 'America/New_York', // Panhandle west of Apalachicola is Central
  GA: 'America/New_York',
  GU: 'Pacific/Guam',
  HI: 'Pacific/Honolulu',
  ID: 'America/Boise', // North Idaho is Pacific (see notes)
  IL: 'America/Chicago',
  IN: 'America/Indiana/Indianapolis', // Some counties are Central
  IA: 'America/Chicago',
  KS: 'America/Chicago', // Some western counties are Mountain
  KY: 'America/New_York', // Western KY is Central
  LA: 'America/Chicago',
  ME: 'America/New_York',
  MH: 'Pacific/Majuro', // Kwajalein also UTC+12
  MD: 'America/New_York',
  MA: 'America/New_York',
  MI: 'America/Detroit', // Western UP has Central
  MN: 'America/Chicago',
  MS: 'America/Chicago',
  MO: 'America/Chicago',
  MT: 'America/Denver',
  NE: 'America/Chicago', // Western NE is Mountain
  NV: 'America/Los_Angeles', // (West Wendover observes Mountain socially)
  NH: 'America/New_York',
  NJ: 'America/New_York',
  NM: 'America/Denver',
  NY: 'America/New_York',
  NC: 'America/New_York',
  ND: 'America/Chicago', // SW ND is Mountain
  MP: 'Pacific/Saipan',
  OH: 'America/New_York',
  OK: 'America/Chicago',
  OR: 'America/Los_Angeles', // SE OR (Malheur/Harney parts) is Mountain
  PW: 'Pacific/Palau',
  PA: 'America/New_York',
  PR: 'America/Puerto_Rico',
  RI: 'America/New_York',
  SC: 'America/New_York',
  SD: 'America/Chicago', // West SD (incl. Rapid City) is Mountain
  TN: 'America/Chicago', // Majority (Nashville, Memphis) is Central; East TN (Knoxville, Chattanooga) is Eastern. See #29931.
  TX: 'America/Chicago', // El Paso & Hudspeth are Mountain
  UT: 'America/Denver',
  VT: 'America/New_York',
  VI: 'America/St_Thomas',
  VA: 'America/New_York',
  WA: 'America/Los_Angeles',
  WV: 'America/New_York',
  WI: 'America/Chicago',
  WY: 'America/Denver'
};

/**
 * US-state → display abbreviation for the state's majority timezone. Used for
 * customer-visible doc rendering (policy applications, ID cards, etc.).
 *
 * Source of truth: the State_Timezone sheet attached to issue #29931. For
 * multi-zone states we lock to the zone covering the majority of population
 * (e.g. MI → ET, NE → CT, IN → ET, ND/SD → CT). AZ shows MT but does not
 * observe DST — pair this with `stateToTzMap` (which uses America/Phoenix)
 * when rendering actual timestamps.
 */
export const stateToTzAbbreviationMap: Record<string, string> = {
  AL: 'CT',
  AK: 'AT',
  AZ: 'MT', // Mountain Standard Time year-round (no DST)
  AR: 'CT',
  CA: 'PT',
  CO: 'MT',
  CT: 'ET',
  DE: 'ET',
  DC: 'ET',
  FL: 'ET',
  GA: 'ET',
  HI: 'HT',
  ID: 'MT',
  IL: 'CT',
  IN: 'ET',
  IA: 'CT',
  KS: 'CT',
  KY: 'ET',
  LA: 'CT',
  ME: 'ET',
  MD: 'ET',
  MA: 'ET',
  MI: 'ET',
  MN: 'CT',
  MS: 'CT',
  MO: 'CT',
  MT: 'MT',
  NE: 'CT',
  NV: 'PT',
  NH: 'ET',
  NJ: 'ET',
  NM: 'MT',
  NY: 'ET',
  NC: 'ET',
  ND: 'CT',
  OH: 'ET',
  OK: 'CT',
  OR: 'PT',
  PA: 'ET',
  RI: 'ET',
  SC: 'ET',
  SD: 'CT',
  TN: 'CT',
  TX: 'CT',
  UT: 'MT',
  VT: 'ET',
  VA: 'ET',
  WA: 'PT',
  WV: 'ET',
  WI: 'CT',
  WY: 'MT'
};

/**
 * Calculates if the current time is within calling hours.
 * Returns 1 if both conditions are met:
 * - Current time is between 8:30am-9pm Eastern Time
 * - Current time is between 8am-9pm in the state's local time
 * Otherwise returns 0
 *
 * Both windows are wall-clock based. DST is handled automatically: `utcToZonedTime`
 * resolves each IANA zone (e.g. `America/Chicago`, `America/Phoenix`) to the right
 * UTC offset for the current moment, including DST transitions and never-DST zones
 * like Arizona. We do not need to special-case standard vs daylight time.
 *
 * Because the state→zone map locks each state to its majority zone (see #29931),
 * a customer physically inside a non-majority part of the state is evaluated as if
 * they live in the majority zone. The window is still wall-clock based — it just
 * uses the majority's wall clock. Acceptable tradeoff per Slack discussion linked
 * from #29931.
 *
 * @param state - Two-letter US state abbreviation
 * @returns 1 if within calling hours, 0 otherwise
 */
export function calculateDuringCallingHours(state?: string): 0 | 1 {
  try {
    if (!state) {
      return 0;
    }

    const now = new Date();

    // Check Eastern time (8:30am - 9pm ET) because this is the time when we have people available to make the calls
    // (it's not enough to just check the local time zone, because we need people on our side too)
    const easternTime = utcToZonedTime(now, 'America/New_York');
    const easternHour = parseInt(format(easternTime, 'H'), 10);
    const easternMinute = parseInt(format(easternTime, 'm'), 10);
    const easternMinutesSinceMidnight = easternHour * 60 + easternMinute;
    const SALES_HOURS_START_MINUTES = 8 * 60 + 30; // 8:30am ET
    const SALES_HOURS_END_MINUTES = 21 * 60; // 9:00pm ET
    const isWithinEasternHours =
      easternMinutesSinceMidnight >= SALES_HOURS_START_MINUTES && easternMinutesSinceMidnight < SALES_HOURS_END_MINUTES;

    if (!isWithinEasternHours) {
      return 0;
    }

    // Check local time for the state (8am - 9pm local)
    const localTimeZone = stateToTzMap[state];
    if (!localTimeZone) {
      console.warn(`No timezone mapping found for state: ${state}`);
      return 0;
    }

    const localTime = utcToZonedTime(now, localTimeZone);
    const localHour = parseInt(format(localTime, 'H'), 10);
    const isWithinLocalHours = localHour >= 8 && localHour < 21;

    return isWithinLocalHours ? 1 : 0;
  } catch (error) {
    console.error('Error calculating duringCallingHours:', error);
    return 0;
  }
}
